import { createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * ThemeContext
 * ----------------------------------------------------------------------
 * Single source of truth for the GroovLens light/dark theme.
 *
 * Priority order on load:
 *   1. user.theme_preference from the backend (if logged in)
 *   2. localStorage('groovlens:theme')
 *   3. system (prefers-color-scheme)
 *   4. fallback: 'dark'
 *
 * The provider mounts the resolved theme onto the document root by toggling
 * the 'dark' class — Tailwind's darkMode: 'class' picks it up automatically.
 */
const ThemeContext = createContext({
  theme: 'dark',
  preference: 'dark',
  setPreference: () => {},
  toggle: () => {},
});

const LS_KEY = 'groovlens:theme';

function resolveSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveActualTheme(preference) {
  if (preference === 'system') return resolveSystemTheme();
  return preference === 'light' ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem(LS_KEY) || 'dark';
  });
  const [theme, setTheme] = useState(() => resolveActualTheme(preference));

  // Apply 'dark' class to document root whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Also update <meta name='theme-color'> so PWA chrome matches
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#0B0F14' : '#FFFFFF');
    }
  }, [theme]);

  // Re-resolve when preference changes
  useEffect(() => {
    setTheme(resolveActualTheme(preference));
  }, [preference]);

  // Listen for system-theme changes when preference === 'system'
  useEffect(() => {
    if (preference !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setTheme(resolveSystemTheme());
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setPreference = useCallback((pref) => {
    if (!['dark', 'light', 'system'].includes(pref)) return;
    localStorage.setItem(LS_KEY, pref);
    setPreferenceState(pref);
  }, []);

  const toggle = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setPreference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
